import React, { useState, useEffect } from 'react';
import { FileText, Send, Clock, CheckCircle, AlertCircle, Calendar, Users, DollarSign, Info, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BoxOfficeReports = ({ cinemaId, onReportSubmitted }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReport, setExpandedReport] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchReports();
  }, [cinemaId]);

  const fetchReports = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/box-office-reports/cinema/${cinemaId}`);
      if (res.ok) {
        const data = await res.json();
        setReports(data);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
    setLoading(false);
  };

  const handleSubmitReport = async (reportId) => {
    setSubmitting(reportId);
    try {
      const data = formData[reportId] || {};
      const params = new URLSearchParams();
      
      if (data.no_report_flat_rate) {
        params.append('no_report_flat_rate', 'true');
      } else {
        if (data.spectators) params.append('spectators', data.spectators);
        if (data.revenue) params.append('revenue', data.revenue);
      }
      
      const res = await fetch(`${BACKEND_URL}/api/box-office-reports/${reportId}/submit?${params}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        fetchReports();
        setExpandedReport(null);
        setFormData(prev => ({ ...prev, [reportId]: {} }));
        if (onReportSubmitted) onReportSubmitted();
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Error al enviar el reporte');
    }
    setSubmitting(null);
  };

  const updateFormData = (reportId, field, value) => {
    setFormData(prev => ({
      ...prev,
      [reportId]: {
        ...(prev[reportId] || {}),
        [field]: value
      }
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'reported':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" />
            Reportado
          </span>
        );
      case 'reminder_sent':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">
            <AlertCircle className="w-3 h-3" />
            Recordatorio enviado
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs">
            <Clock className="w-3 h-3" />
            Pendiente
          </span>
        );
    }
  };

  const isPastScreening = (date) => {
    const screeningDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return screeningDate <= today;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Sort reports: pending past screenings first, then by date descending
  const sortedReports = [...reports].sort((a, b) => {
    const aIsPast = isPastScreening(a.screening_date);
    const bIsPast = isPastScreening(b.screening_date);
    const aIsPending = a.status === 'pending';
    const bIsPending = b.status === 'pending';
    
    // Priority: Past + Pending first
    if (aIsPast && aIsPending && !(bIsPast && bIsPending)) return -1;
    if (bIsPast && bIsPending && !(aIsPast && aIsPending)) return 1;
    
    // Then by date descending
    return new Date(b.screening_date) - new Date(a.screening_date);
  });

  // Separate pending reports that need attention
  const pendingReports = sortedReports.filter(r => r.status === 'pending' && isPastScreening(r.screening_date));
  const otherReports = sortedReports.filter(r => !(r.status === 'pending' && isPastScreening(r.screening_date)));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white/50">Cargando reportes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FileText className="w-6 h-6 text-[#e4002b]" />
            Reportes de Taquilla
          </h2>
          <p className="text-white/50 text-sm mt-1">
            Reporta los datos de taquilla de tus pases realizados
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-300">
            <p className="font-medium mb-1">¿Cómo funciona?</p>
            <p className="text-blue-400/80">
              Después de cada pase contratado, aparecerá aquí para que puedas reportar el número de 
              espectadores y la recaudación. Si el pase es a tanto alzado, marca la opción correspondiente.
            </p>
          </div>
        </div>
      </div>

      {/* Pending Reports (Need Attention) */}
      {pendingReports.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-400" />
            Pendientes de reportar ({pendingReports.length})
          </h3>
          
          {pendingReports.map(report => (
            <div 
              key={report.id} 
              className="bg-orange-500/10 border border-orange-500/30 rounded-xl overflow-hidden"
            >
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5"
                onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
              >
                <div className="flex items-center gap-4">
                  {report.content_image && (
                    <img 
                      src={report.content_image} 
                      alt="" 
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <div>
                    <h4 className="text-white font-semibold">{report.content_title}</h4>
                    <p className="text-white/60 text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {formatDate(report.screening_date)} - {report.screening_time}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(report.status)}
                  {expandedReport === report.id ? (
                    <ChevronUp className="w-5 h-5 text-white/60" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/60" />
                  )}
                </div>
              </div>

              {/* Expanded Form */}
              {expandedReport === report.id && (
                <div className="p-4 pt-0 border-t border-orange-500/20">
                  <div className="bg-black/30 rounded-lg p-4 mt-3 space-y-4">
                    {/* No Report Option */}
                    <label className="flex items-center gap-3 cursor-pointer p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData[report.id]?.no_report_flat_rate || false}
                        onChange={(e) => updateFormData(report.id, 'no_report_flat_rate', e.target.checked)}
                        className="w-5 h-5 rounded border-white/30 bg-white/10 text-[#e4002b] focus:ring-[#e4002b]"
                      />
                      <div>
                        <span className="text-white font-medium">Sin reporte - Pase a tanto alzado</span>
                        <p className="text-white/50 text-sm">Marca esta opción si el pase fue a tanto alzado</p>
                      </div>
                    </label>

                    {/* Report Fields */}
                    {!formData[report.id]?.no_report_flat_rate && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-white/60 text-sm mb-1 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Nº de Espectadores
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={formData[report.id]?.spectators || ''}
                            onChange={(e) => updateFormData(report.id, 'spectators', e.target.value)}
                            placeholder="0"
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/40 focus:border-[#e4002b] focus:ring-1 focus:ring-[#e4002b]"
                          />
                        </div>
                        <div>
                          <label className="block text-white/60 text-sm mb-1 flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            Recaudación Total (€)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData[report.id]?.revenue || ''}
                            onChange={(e) => updateFormData(report.id, 'revenue', e.target.value)}
                            placeholder="0.00"
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/40 focus:border-[#e4002b] focus:ring-1 focus:ring-[#e4002b]"
                          />
                        </div>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      onClick={() => handleSubmitReport(report.id)}
                      disabled={submitting === report.id}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#e4002b] text-white rounded-lg font-semibold hover:bg-[#c00025] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {submitting === report.id ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Enviar Reporte
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Historical Reports */}
      {otherReports.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-semibold">Histórico de pases</h3>
          
          {otherReports.map(report => (
            <div 
              key={report.id} 
              className="bg-white/5 border border-white/10 rounded-xl p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {report.content_image && (
                    <img 
                      src={report.content_image} 
                      alt="" 
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  )}
                  <div>
                    <h4 className="text-white font-medium">{report.content_title}</h4>
                    <p className="text-white/50 text-sm">
                      {formatDate(report.screening_date)} - {report.screening_time}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {report.status === 'reported' && (
                    <div className="text-right">
                      {report.no_report_flat_rate ? (
                        <span className="text-white/60 text-sm">Tanto alzado</span>
                      ) : (
                        <>
                          <p className="text-white text-sm">
                            <span className="text-white/60">Espectadores:</span> {report.spectators || 0}
                          </p>
                          <p className="text-white text-sm">
                            <span className="text-white/60">Recaudación:</span> {report.revenue || 0}€
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  {getStatusBadge(report.status)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {reports.length === 0 && (
        <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
          <FileText className="w-12 h-12 text-white/30 mx-auto mb-3" />
          <h3 className="text-white font-medium mb-1">No hay reportes pendientes</h3>
          <p className="text-white/50 text-sm">
            Los pases contratados aparecerán aquí una vez se hayan realizado
          </p>
        </div>
      )}
    </div>
  );
};

export default BoxOfficeReports;
